import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Role } from 'shared-lib';
import { ParkingSpaceService, InfrastructureService, ParkingTypeDto, ParkingTypeEnum, CustomExemptionDto, DefaultExemptionDto, ParkingProcessService, ParkingSpaceProcessesSummaryDto, FetchParkingSpaceProcessesRequest, CreateInfrastructureRequest, InfrastructureType } from '../../../api/gen';
import { ParkingSpaceDto } from '../../../api/gen/model/parkingSpaceDto';
import { InfrastructureDto } from '../../../api/gen/model/infrastructureDto';
import { DeviceGroupSpaceConfigComponent } from './device-group-space-config/device-group-space-config.component';
import { ParkingTypeSpaceComponent } from '../parking-type-space/parking-type-space.component';
import { MasterControllerListComponent } from './master-controller-list/master-controller-list.component';
import { QuickEasypassSettingsDialogComponent } from '../quick-easypass/quick-easypass-settings-dialog.component';
import { EasypassModesDialogComponent } from '../easypass-modes/easypass-modes-dialog.component';
import { LatePaymentSettingsDialogComponent } from '../late-payment-settings/late-payment-settings-dialog.component';
import { RoleService } from '../../auth/role-service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-parking-space-details',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        ParkingTypeSpaceComponent,
        TranslatePipe
    ],
    templateUrl: './parking-space-details.component.html',
    styleUrl: './parking-space-details.component.scss'
})
export class ParkingSpaceDetailsComponent implements OnInit {

    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private parkingSpaceService = inject(ParkingSpaceService);
    private infrastructureService = inject(InfrastructureService);
    private parkingProcessService = inject(ParkingProcessService);
    private dialog = inject(MatDialog);
    private snackBar = inject(MatSnackBar);
    private roleService = inject(RoleService);

    parkingSpaceId: string | null = null;
    parkingSpace = signal<ParkingSpaceDto | null>(null);
    infrastructure = signal<InfrastructureDto | null>(null);

    isLoading = signal(false);
    isAdmin = signal(false);

    /** True while an infrastructure is being created for this space. */
    isAddingInfrastructure = signal(false);

    /** Number of device groups configured on the linked infrastructure. */
    deviceGroupCount = computed(() => this.infrastructure()?.deviceGroups?.length ?? 0);

    /** Total cameras across all device groups of the linked infrastructure. */
    totalCameras = computed(() =>
        (this.infrastructure()?.deviceGroups ?? []).reduce(
            (sum, group) => sum + (group.cameras?.length ?? 0), 0)
    );

    /** Selected zone in the live-summary zone selector. */
    selectedZoneId = signal<string | null>(null);

    /** Resolved zone for the current selection (falls back to the first zone). */
    selectedZone = computed(() => {
        const zones = this.parkingSpace()?.parkingZones ?? [];
        return zones.find(z => z.id === this.selectedZoneId()) ?? zones[0] ?? null;
    });

    /** True when the space address carries both latitude and longitude. */
    hasCoordinates = computed(() => {
        const address = this.parkingSpace()?.address;
        return address?.latitude != null && address?.longitude != null;
    });

    /**
     * Restricted-access (visitor) window as "HH:mm – HH:mm", or null when no window is
     * configured. On a Closed space this is the daily period during which unregistered
     * visitors are admitted (and billed as short-term); outside it they are denied.
     */
    restrictedWindow = computed(() => {
        const space = this.parkingSpace();
        if (!space?.restrictedAccessWindowStart || !space?.restrictedAccessWindowEnd) return null;
        return `${this.toHourMinute(space.restrictedAccessWindowStart)} – ${this.toHourMinute(space.restrictedAccessWindowEnd)}`;
    });

    /** Abbreviated days the visitor window applies to ("Mon, Fri"); null = every day. */
    restrictedWindowDays = computed(() => {
        const days = this.parkingSpace()?.restrictedAccessWindowDays;
        if (!days || days.length === 0) return null;
        return days.map(day => day.slice(0, 3)).join(', ');
    });

    /** "17:00:00" (TimeOnly over the wire) → "17:00". */
    private toHourMinute(time: string): string {
        return time.slice(0, 5);
    }

    /** Live parking-process summary for this space (capacity / occupancy). */
    processSummary = signal<ParkingSpaceProcessesSummaryDto | null>(null);
    isLoadingSummary = signal(false);

    /** Total capacity = sum of the capacities of all zones in the space. */
    totalCapacity = computed(() =>
        (this.parkingSpace()?.parkingZones ?? []).reduce((sum, zone) => sum + (zone.capacity ?? 0), 0)
    );

    /** Currently occupied slots = active parking processes for the space. */
    occupiedCount = computed(() => this.processSummary()?.activeProcessCount ?? 0);

    /** Free capacity as a percentage, or null when capacity is unknown. */
    availabilityPercent = computed(() => {
        const capacity = this.totalCapacity();
        if (capacity <= 0) return null;
        const free = Math.max(0, capacity - this.occupiedCount());
        return Math.round((free / capacity) * 100);
    });

    /** Tailwind colour for the availability figure (green / amber / red). */
    availabilityColorClass = computed(() => {
        const available = this.availabilityPercent();
        if (available === null) return 'text-gray-700';
        if (available <= 10) return 'text-red-600';
        if (available <= 30) return 'text-yellow-600';
        return 'text-green-600';
    });

    /** Process summary for the zone currently selected in the dropdown. */
    selectedZoneSummary = computed(() => {
        const zoneId = this.selectedZone()?.id;
        return this.processSummary()?.zoneSummaries?.find(z => z.zoneId === zoneId) ?? null;
    });

    async ngOnInit(): Promise<void> {
        this.parkingSpaceId = this.route.snapshot.paramMap.get('id');
        if (this.parkingSpaceId) {
            this.loadParkingSpace();
        } else {
            this.router.navigate(['/parking-space']);
        }
        this.isAdmin.set(await this.roleService.checkAccess(Role.Administrator));
    }

    private loadParkingSpace(): void {
        if (!this.parkingSpaceId) return;

        this.isLoading.set(true);
        this.parkingSpaceService.parkingSpaceParkingSpaceIdGet(this.parkingSpaceId).subscribe({
            next: (result) => {
                if (result) {
                    this.parkingSpace.set(result);
                    this.selectedZoneId.set(result.parkingZones?.[0]?.id ?? null);
                    this.loadProcessSummary(this.parkingSpaceId!);
                    if (result.parkingSpaceInfrastructureId) {
                        this.loadInfrastructure(result.parkingSpaceInfrastructureId);
                    }
                }
                this.isLoading.set(false);
            },
            error: (error) => {
                console.error('Error loading parking space:', error);
                this.snackBar.open('Failed to load parking space details', 'Close', { duration: 3000 });
                this.isLoading.set(false);
            }
        });
    }

    private loadInfrastructure(infrastructureId: string): void {
        this.infrastructureService.infrastructuresInfrastructureIdGet(infrastructureId).subscribe({
            next: (result) => {
                if (result) {
                    this.infrastructure.set(result);
                }
            },
            error: (error) => {
                console.error('Error loading infrastructure:', error);
            }
        });
    }

    private loadProcessSummary(parkingSpaceId: string): void {
        this.isLoadingSummary.set(true);
        const request: FetchParkingSpaceProcessesRequest = {
            pageIndex: 0,
            pageSize: 1,
            includeDeleted: false,
        };
        this.parkingProcessService.parkingProcessParkingSpaceParkingSpaceIdPost(parkingSpaceId, request).subscribe({
            next: (result) => {
                this.processSummary.set(result);
                this.isLoadingSummary.set(false);
            },
            error: (error) => {
                console.error('Error loading parking process summary:', error);
                this.isLoadingSummary.set(false);
            }
        });
    }

    /**
     * Creates infrastructure for this space and links it (only needs the space id +
     * type; the backend wires up the association). Used by the empty-state action when
     * the space has no infrastructure yet — the create wizard was previously the only
     * place this could happen.
     */
    addInfrastructure(): void {
        if (!this.parkingSpaceId) {
            this.snackBar.open('Parking space ID not found.', 'Close', { duration: 3000 });
            return;
        }
        if (this.infrastructure() || this.isAddingInfrastructure()) {
            return;
        }

        const request: CreateInfrastructureRequest = {
            parkingSpaceId: this.parkingSpaceId,
            infrastructureType: InfrastructureType.ParkingSpace,
        };

        this.isAddingInfrastructure.set(true);
        this.infrastructureService.infrastructuresPost(request).subscribe({
            next: (result) => {
                this.infrastructure.set(result);
                this.parkingSpace.update(space =>
                    space ? { ...space, parkingSpaceInfrastructureId: result.id } : space);
                this.snackBar.open('Infrastructure added successfully.', 'Close', { duration: 3000 });
                this.isAddingInfrastructure.set(false);
            },
            error: (error) => {
                console.error('Error adding infrastructure:', error);
                this.snackBar.open('Failed to add infrastructure. Please try again.', 'Close', { duration: 5000 });
                this.isAddingInfrastructure.set(false);
            }
        });
    }

    configureInfrastructure(): void {
        if (!this.infrastructure()) {
            this.snackBar.open('Infrastructure not loaded yet.', 'Close', { duration: 3000 });
            return;
        }
        const dialogRef = this.dialog.open(DeviceGroupSpaceConfigComponent, {
            width: '900px',
            maxWidth: '95vw',
            data: {
                infrastructure: this.infrastructure()
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && this.parkingSpace()?.parkingSpaceInfrastructureId) {
                this.loadInfrastructure(this.parkingSpace()!.parkingSpaceInfrastructureId!);
            }
        });
    }

    /**
     * Opens the late-payment settings editor (KAN-458 D-37).
     *
     * The dialog returns the saved space, but this reloads from the server instead of trusting it:
     * these four values decide whether parkers get billed after they have driven away, so the card
     * should show what the database holds, not what the client believes it sent.
     */
    editLatePaymentSettings(): void {
        const space = this.parkingSpace();
        if (!space) return;

        const dialogRef = this.dialog.open(LatePaymentSettingsDialogComponent, {
            width: '560px',
            maxWidth: '95vw',
            data: { parkingSpace: space }
        });

        dialogRef.afterClosed().subscribe(saved => {
            if (saved) {
                this.loadParkingSpace();
                this.snackBar.open('Late-payment settings updated.', 'Close', { duration: 3000 });
            }
        });
    }

    /** Opens the Quick Easypass settings editor; reloads from the server after a save, like late payment. */
  editQuickEasypassSettings(): void {
    const space = this.parkingSpace();
    if (!space) return;

    const dialogRef = this.dialog.open(QuickEasypassSettingsDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      data: { parkingSpace: space }
    });

    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.loadParkingSpace();
        this.snackBar.open('Quick Easypass settings updated.', 'Close', { duration: 3000 });
      }
    });
  }

  /** Opens the Easypass mode switches (Administrator only); reloads from the server after a save. */
  editEasypassModes(): void {
    const space = this.parkingSpace();
    if (!space) return;

    const dialogRef = this.dialog.open(EasypassModesDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      data: { parkingSpace: space }
    });

    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.loadParkingSpace();
      }
    });
  }

  configureMasterControllers(): void {
        if (!this.infrastructure()) {
            this.snackBar.open('Infrastructure not loaded yet.', 'Close', { duration: 3000 });
            return;
        }

        if (!this.parkingSpaceId) {
            this.snackBar.open('Parking space ID not found.', 'Close', { duration: 3000 });
            return;
        }

        const dialogRef = this.dialog.open(MasterControllerListComponent, {
            width: '900px',
            maxWidth: '95vw',
            data: {
                infrastructure: this.infrastructure(),
                parkingSpaceId: this.parkingSpaceId
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && this.parkingSpace()?.parkingSpaceInfrastructureId) {
                this.loadInfrastructure(this.parkingSpace()!.parkingSpaceInfrastructureId!);
            }
        });
    }

    goBack(): void {
        this.router.navigate(['/parking-space']);
    }

    editParkingSpace(): void {
        if (this.parkingSpaceId) {
            this.router.navigate(['/parking-space', this.parkingSpaceId, 'edit']);
        }
    }
}
